<template>
  <div class="nx-form-bulk-editable-field">
    <v-layout
      row
      class="mb-2"
    >
      <component
        :is="preparedFieldConfig.component"
        ref="field"
        class="grow"
        :label="preparedFieldConfig.label"
        :name="preparedFieldConfig.name"
        :value="fieldValue"
        :validation="preparedFieldConfig.validation"
        :disabled="preparedFieldConfig.params.disabled || !showField"
        v-bind="preparedFieldConfig.params"
        v-on="$listeners"
      />

      <v-select
        :label="actionLabel"
        outline
        hide-details
        class="action shrink ml-5"
        :items="actionItems"
        :value="actionValue"
        @input="updateActionValue"
      />
    </v-layout>
  </div>
</template>

<script>
import PrepareFieldsConfig from '@/modules/form/mixins/prepare-fields-config';
import FormElement from '@/modules/form/mixins/form-element';

const ACTION_ADD = 'add';
const ACTION_KEEP = 'keep';
const ACTION_MODIFY = 'modify';
const ACTION_CLEAR = 'clear';

const ADD_FIELDS = 'addFields';
const MODIFY_FIELDS = 'modifyFields';
const CLEAR_FIELDS = 'clearFields';

export default {
  name: 'NxFormBulkEditableField',
  mixins: [PrepareFieldsConfig, FormElement],
  props: {
    fieldConfig: {
      type: Object,
      required: true,
    },
    formValue: {
      type: Object,
      required: true,
    },
    clearAction: {
      type: Boolean,
      default: true,
    },
    addAction: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      actionValue: ACTION_KEEP,
    };
  },
  computed: {
    actionItems() {
      const actions = [
        { text: this.$t('message.searcher.bulk.editAction.keep'), value: ACTION_KEEP },
        { text: this.$t('message.searcher.bulk.editAction.modify'), value: ACTION_MODIFY },
      ];
      if (this.addAction) {
        actions.push({ text: this.$t('message.searcher.bulk.editAction.add'), value: ACTION_ADD });
      }
      if (this.clearAction) {
        actions.push({ text: this.$t('message.searcher.bulk.editAction.clear'), value: ACTION_CLEAR });
      }
      return actions;
    },
    actionLabel() {
      return this.$t('message.searcher.bulk.editAction.actionLabel', { field: this.preparedFieldConfig.label });
    },
    key() {
      return `editable-action-field.${this.field}`;
    },
    field() {
      return this.fieldConfig.field;
    },
    fieldValue() {
      return this.formValue[this.field];
    },
    preparedFieldConfig() {
      return this.prepareFieldConfig(this.fieldConfig, this.key, this.scope);
    },
    showField() {
      return this.actionValue === ACTION_ADD || this.actionValue === ACTION_MODIFY;
    },
    clearFields() {
      return this.formValue.clearFields || [];
    },
    addFields() {
      return this.formValue.addFields || [];
    },
    modifyFields() {
      return this.formValue.modifyFields || [];
    },
  },
  methods: {
    async updateActionValue(action) {
      const actionToFields = {
        [ACTION_ADD]: ADD_FIELDS,
        [ACTION_MODIFY]: MODIFY_FIELDS,
        [ACTION_KEEP]: null,
        [ACTION_CLEAR]: CLEAR_FIELDS,
      };

      // Reset action
      const clearFields = new Set(this.clearFields);
      clearFields.delete(this.field);
      this.$parentForm.updateField(CLEAR_FIELDS, Array.from(clearFields));

      const modifyFields = new Set(this.modifyFields);
      modifyFields.delete(this.field);
      this.$parentForm.updateField(MODIFY_FIELDS, Array.from(modifyFields));

      const addFields = new Set(this.addFields);
      addFields.delete(this.field);
      this.$parentForm.updateField(ADD_FIELDS, Array.from(addFields));

      // Update action
      const fields = new Set([
        ...this.formValue[actionToFields[action]] || [],
        this.field,
      ]);
      this.actionValue = action;
      if (actionToFields[action]) {
        this.$parentForm.updateField(actionToFields[action], Array.from(fields));
      }

      if (action === ACTION_CLEAR || action === ACTION_KEEP) {
        this.$parentForm.updateField(this.field, undefined);
      }
    },
  },
};
</script>

<style lang="stylus">
.nx-form-bulk-editable-field {
  .action {
    width: 260px;
  }
  .v-chip .v-chip__content > * {
    max-width: 35vw;
    overflow: hidden;
    text-overflow: ellipsis;
    direction: rtl;
  }
}
</style>
